/**
 * What Studio V2's "Anfordern" hands the inquiry composer (TUR-1211, Erik 02.09
 * bug 3: "the Request button ignores the review context").
 *
 * #2120 already threads the matched supplier and the framework's open article
 * ids. This adds the rest of the context the reviewer is standing in — the
 * active Verordnung (so the composer preselects ONE inquiry framework instead
 * of all of them), the declaration/statement being reviewed (persisted on the
 * inquiry as `source_*`, the same fields the statement-level Rückfrage sets)
 * and one human-readable line the composer prepends to the mail draft under a
 * "Bezug:" label (TUR-1211, tester finding, Pablo/Notion item 8: appended at
 * the bottom it went unnoticed, so it now leads the draft), so the supplier
 * is told which declaration and which gap the questionnaire is about.
 *
 * Everything travels as URL params: the composer is a separate route, and the
 * old bare navigate must keep working for every caller that has no context.
 */

import { regulationKeyFor } from "../../../../../utils/inquiryScope"

export interface RequestContext {
  declarationId: string
  /** The DEC-YYYY-NNNN reference, when the list row carries it. */
  declarationNumber: string | null
  /** The active Verordnung's declaration-side slug. */
  framework: string | null
  /** The statement the reviewer is on (URL focus, else the first open one). */
  statementId: string | null
  /** The one-line "Bezug" the composer appends to the mail draft, already
   *  translated by the caller. Null ⇒ nothing is appended. */
  line: string | null
}

/** The composer's URL params for one Anfordern press. `supplierIds` and
 *  `articleIds` are #2120's existing contract, unchanged; everything else is
 *  additive and only present when known. */
export function buildRequestSearchParams(input: {
  supplierId?: string | null
  articleIds?: readonly string[]
  context?: RequestContext | null
  /** The regulations the inquiry picker preselects, as keys, in place of the
   *  context framework's own: what an inquiry's replies still lack (TUR-1732). */
  regulations?: readonly string[]
}): URLSearchParams {
  const params = new URLSearchParams()
  if (input.supplierId) params.append("supplierIds", input.supplierId)
  for (const id of input.articleIds ?? []) params.append("articleIds", id)
  for (const key of input.regulations ?? []) params.append("regulations", key)
  const ctx = input.context
  if (!ctx) return params
  params.append("declarationId", ctx.declarationId)
  if (ctx.framework) {
    params.append("framework", ctx.framework)
    // The regulation the inquiry picker preselects, as its key, for every framework
    // (TUR-1730): the slug maps through the same aliases as every label.
    const regulation = regulationKeyFor(ctx.framework)
    if (regulation && !input.regulations)
      params.append("regulations", regulation)
  }
  if (ctx.statementId) params.append("statementId", ctx.statementId)
  if (ctx.line) params.append("context", ctx.line)
  return params
}

/** The mail draft body with the "Bezug" line PREPENDED as its own paragraph,
 *  first, never last (tester finding, Pablo/Notion item 8: buried at the
 *  bottom under the standard template, he never noticed which declaration
 *  the questionnaire was about). `line` already names the declaration
 *  (`declarationNumber`), the Verordnung and the statement, built once in
 *  `StudioV2Layout`, see `RequestContext.line`'s own doc, so putting it
 *  first is the whole fix; the standard template text is unchanged below
 *  it. */
export function withContextParagraph(
  body: string,
  label: string,
  line: string,
): string {
  const trimmed = body.trim()
  const paragraph = `${label}: ${line}`
  return trimmed ? `${paragraph}\n\n${trimmed}` : paragraph
}
